/**
 * Durable per-tool undo snapshots.
 *
 * Tool undo is normally driven by the in-memory command object a tool stashes at
 * apply time (`current*Command`). Those commands close over live BufferGeometry,
 * so they cannot survive a reload — and `restoreSessionState` / the draft loader
 * deliberately null them out. Before this module the applied tool card still
 * rendered an *enabled* Undo button in that state, and clicking it silently did
 * nothing.
 *
 * So: at apply time we also snapshot the socket exactly as it was *before* the
 * tool ran, keyed by tool id. Within a session the pool below outlives step
 * navigation on its own; across a reload it rides along in the step scene cache
 * (IndexedDB). When the live command is gone, undo restores from the snapshot.
 *
 * The pool is deliberately keyed by tool id alone (not step+tool): a tool applies
 * at most once per design, and undo always means "put the socket back the way it
 * was before *this* tool", which is exactly one geometry per tool.
 */
import * as THREE from 'three';
import useEditorStore from '../../shared/stores/useEditorStore';
import { serializeMaterial, deserializeMaterial } from '../../shared/lib/serialization';
import { toolIdsForStep } from './undo/undoManifestStore';
import { serializeGeometry, deserializeGeometry } from './meshSnapshot';
import { stripPreviewTint } from '../../shared/lib/previewTint';
import useUndoSnapshotStore from './undoSnapshotStore';

const LOG_PREFIX = '[UndoSnapshot]';

/** Bump when the payload shape below changes; older cached entries are ignored. */
export const UNDO_SNAPSHOT_SCHEMA_VERSION = 1;

/**
 * @typedef {Object} UndoSnapshotPayload
 * @property {THREE.BufferGeometry} geometry  pre-apply socket geometry
 * @property {number[]} position
 * @property {number[]} quaternion
 * @property {number[]} scale
 * @property {string} meshName
 * @property {object} userData
 * @property {object|null} materialData
 */

/** @type {Map<string, UndoSnapshotPayload>} */
const pool = new Map();

function cloneUserData(userData) {
  if (!userData || typeof userData !== 'object') return {};
  try {
    return JSON.parse(JSON.stringify(userData));
  } catch {
    return { ...userData };
  }
}

function disposePayload(payload) {
  payload?.geometry?.dispose?.();
}

/**
 * Snapshot the current socket as the pre-apply state for `toolId`.
 * Call this *before* the tool mutates/replaces the shaded mesh.
 *
 * @param {string} toolId
 * @returns {boolean} whether a snapshot was taken
 */
export function captureUndoSnapshot(toolId) {
  if (!toolId) return false;

  const { sceneRefs } = useEditorStore.getState();
  const shadedMesh = sceneRefs?.shadedMesh;
  if (!shadedMesh?.geometry?.attributes?.position?.count) return false;

  // Write-once per tool. A tool re-applied with new settings (thickness nudged
  // 4mm → 5mm → 6mm) keeps the socket from before its *first* apply, because that
  // is what undoing it means everywhere else: `resetApplied` returns the card to
  // fully un-applied, the durable before-mesh is written once, and the manifest
  // truncates from the first apply. Overwriting here would restore the socket to
  // one nudge back while telling the user the tool was never applied.
  //
  // `clearUndoSnapshot` runs on undo and on step revert, so re-applying after an
  // undo captures afresh.
  if (pool.has(toolId)) return false;

  const material = sceneRefs?.material ?? shadedMesh.material;

  // A tool preview may be tinting the socket's vertex colours at this very moment
  // — measure reduction paints its band zones straight onto them, and this capture
  // runs *before* the apply that turns the preview off. This clone is what Undo and
  // "discard and go back" reinstall, and what the before-mesh uploader ships, so a
  // captured tint would come back as the socket's own colour and never wash out.
  const geometry = shadedMesh.geometry.clone();
  stripPreviewTint(geometry);

  pool.set(toolId, {
    geometry,
    position: shadedMesh.position.toArray(),
    quaternion: shadedMesh.quaternion.toArray(),
    scale: shadedMesh.scale.toArray(),
    meshName: shadedMesh.name || 'ShadedMesh',
    userData: cloneUserData(shadedMesh.userData),
    materialData: material ? serializeMaterial(material) : null,
  });
  useUndoSnapshotStore.getState().markAvailable(toolId);

  return true;
}

/** @param {string} toolId */
export function hasUndoSnapshot(toolId) {
  return !!toolId && pool.has(toolId);
}

/**
 * Read a snapshot without consuming it. The uploader needs the pre-apply payload
 * it can pack and ship; it must not disturb the pool.
 *
 * @param {string} toolId
 * @returns {UndoSnapshotPayload | null}
 */
export function peekUndoSnapshot(toolId) {
  return pool.get(toolId) ?? null;
}

/**
 * Put an externally-sourced payload into the pool — a blob downloaded from the
 * server on a device that never performed the apply.
 *
 * Routing a download through the pool (rather than installing it directly) means
 * the server path and the in-session path share one restore implementation, so
 * the transform/material handling in {@link restoreUndoSnapshot} cannot drift
 * between them. An existing in-memory entry always wins: it is the live one.
 *
 * @param {string} toolId
 * @param {UndoSnapshotPayload} payload
 * @returns {boolean} whether the payload was adopted
 */
export function adoptUndoSnapshot(toolId, payload) {
  if (!toolId || !payload?.geometry?.attributes?.position?.count) return false;
  if (pool.has(toolId)) return false;
  pool.set(toolId, {
    geometry: payload.geometry,
    position: payload.position ?? [0, 0, 0],
    quaternion: payload.quaternion ?? [0, 0, 0, 1],
    scale: payload.scale ?? [1, 1, 1],
    meshName: payload.meshName ?? 'ShadedMesh',
    userData: cloneUserData(payload.userData),
    materialData: payload.materialData ?? null,
  });
  useUndoSnapshotStore.getState().markAvailable(toolId);
  return true;
}

/**
 * Reinstall the pre-apply socket for `toolId` as the editable shaded mesh.
 *
 * The live viewer material is kept (undo restores geometry, not appearance); the
 * serialized material is only a fallback for when the scene has none.
 *
 * @param {string} toolId
 * @param {{ reframeCompareBaseline?: boolean }} [opts]
 *   reframeCompareBaseline — the tool baked a different local frame into the
 *   vertices, so the compare overlay has to be re-expressed in the restored mesh's
 *   frame to stay put. Mirrors what the tool's own `undo` passes; only the locking
 *   attachment (world-baked CSG output) needs it.
 * @returns {boolean} whether the socket was restored
 */
export function restoreUndoSnapshot(toolId, { reframeCompareBaseline = false } = {}) {
  const payload = pool.get(toolId);
  if (!payload) return false;

  const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();
  if (!sceneRefs?.scene || !sceneRefs?.shadedMesh) {
    console.warn(`${LOG_PREFIX} cannot restore ${toolId} — scene refs missing`);
    return false;
  }

  const geometry = payload.geometry.clone();
  const hasVertexColors = !!geometry.attributes.color;

  // Same material handoff every tool's own undo uses: hand `replaceShadedMesh` the
  // current viewer material and let it own the swap (it clones the outgoing mesh's
  // material onto the new one and re-runs ghost/colour modes). Deliberately *not*
  // `keepNewMeshMaterial` — that path disposes the outgoing mesh's material, which
  // is the shared `normalMaterial` whenever normal mode is on.
  let material = sceneRefs.material ?? sceneRefs.shadedMesh.material ?? null;
  if (!material && payload.materialData) material = deserializeMaterial(payload.materialData);
  if (!material) {
    material = new THREE.MeshLambertMaterial({
      color: hasVertexColors ? 0xffffff : 0xe8d5b7,
      side: THREE.DoubleSide,
      vertexColors: hasVertexColors,
    });
  }

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = payload.meshName;
  mesh.position.fromArray(payload.position);
  mesh.quaternion.fromArray(payload.quaternion);
  mesh.scale.fromArray(payload.scale);
  mesh.matrixAutoUpdate = true;
  mesh.updateMatrixWorld(true);
  mesh.userData = cloneUserData(payload.userData);

  // keepNewMeshTransform: the snapshot's own TRS *is* the pre-apply transform —
  // letting it be copied off the current mesh would re-apply the transform the tool
  // baked in (locking CSG in particular writes world-baked vertices at identity).
  replaceShadedMesh(mesh, { keepNewMeshTransform: true, reframeCompareBaseline });

  console.log(`${LOG_PREFIX} restored pre-apply socket for ${toolId}`);
  return true;
}

/** @param {string} toolId */
export function clearUndoSnapshot(toolId) {
  const payload = pool.get(toolId);
  if (!payload) return;
  disposePayload(payload);
  pool.delete(toolId);
  useUndoSnapshotStore.getState().markUnavailable(toolId);
}

export function clearAllUndoSnapshots() {
  for (const payload of pool.values()) disposePayload(payload);
  pool.clear();
  useUndoSnapshotStore.getState().clearAll();
}

/**
 * Drop snapshots for every tool on `stepIds`. Used when a backward revert throws
 * away the work on later steps: those tools are no longer applied, so keeping
 * their snapshots would let a stale Undo rewind the socket past the revert.
 *
 * @param {string[]} stepIds
 */
export function clearUndoSnapshotsForSteps(stepIds) {
  for (const stepId of stepIds ?? []) {
    // Recorded list, not this build's — a tool reassigned to another step since
    // this design was worked on still has a snapshot scoped to the step it was
    // applied on, and leaving it behind is exactly the stale Undo this guards.
    for (const toolId of toolIdsForStep(stepId)) {
      clearUndoSnapshot(toolId);
    }
  }
}

/**
 * Pool → structured-clone-safe object for IndexedDB.
 *
 * `stepId` scopes the output to that step's own tools, and callers should always
 * pass it: a snapshot is only ever needed by the step whose card offers the Undo,
 * and serializing the whole pool into every step record would store each tool's
 * pre-apply geometry once per remaining step — tens of megabytes multiplied out
 * until the origin hits its storage quota.
 *
 * @param {string} [stepId] — restrict to the tools on this workflow step.
 * @returns {{ schemaVersion: number, byTool: Record<string, object> } | null}
 */
export function serializeUndoSnapshots(stepId) {
  if (pool.size === 0) return null;
  const scope = stepId ? new Set(toolIdsForStep(stepId)) : null;
  const byTool = {};
  for (const [toolId, payload] of pool.entries()) {
    if (scope && !scope.has(toolId)) continue;
    const geometry = serializeGeometry(payload.geometry);
    if (!geometry) continue;
    byTool[toolId] = {
      geometry,
      position: payload.position,
      quaternion: payload.quaternion,
      scale: payload.scale,
      meshName: payload.meshName,
      userData: payload.userData,
      materialData: payload.materialData,
    };
  }
  if (Object.keys(byTool).length === 0) return null;
  return { schemaVersion: UNDO_SNAPSHOT_SCHEMA_VERSION, byTool };
}

/**
 * Rebuild the pool from a persisted record, so undo works on the first click
 * after a reload. Entries already in memory win — they are the live ones.
 *
 * @param {ReturnType<typeof serializeUndoSnapshots>} serialized
 * @returns {number} how many snapshots were hydrated
 */
export function hydrateUndoSnapshots(serialized) {
  if (!serialized?.byTool) return 0;
  if (serialized.schemaVersion !== UNDO_SNAPSHOT_SCHEMA_VERSION) {
    console.warn(`${LOG_PREFIX} ignoring cache — schema ${serialized.schemaVersion}`);
    return 0;
  }

  let count = 0;
  for (const [toolId, entry] of Object.entries(serialized.byTool)) {
    if (pool.has(toolId)) continue;
    const geometry = deserializeGeometry(entry?.geometry);
    if (!geometry) continue;
    pool.set(toolId, {
      geometry,
      position: entry.position ?? [0, 0, 0],
      quaternion: entry.quaternion ?? [0, 0, 0, 1],
      scale: entry.scale ?? [1, 1, 1],
      meshName: entry.meshName ?? 'ShadedMesh',
      userData: cloneUserData(entry.userData),
      materialData: entry.materialData ?? null,
    });
    count += 1;
  }
  if (count) {
    useUndoSnapshotStore.getState().setAvailable(Array.from(pool.keys()));
    console.log(`${LOG_PREFIX} hydrated ${count} snapshot(s) from cache`);
  }
  return count;
}
